import { ClassValue } from 'clsx';
import { isFunction } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import AsyncReactSelect from 'react-select/async';
import { StateManagerProps } from 'react-select/dist/declarations/src/useStateManager';
import { PropsWithClassName } from 'src/shared/utils/types/PropsWithClassName';

import { Icon } from '@shared/ui/components/Icon';
import { Option, Options, OptionsProvider } from '@shared/ui/components/Select/Select.types';
import { cn } from '@shared/utils/helpers';

type SelectProps<T> = {
  options: OptionsProvider<T>;
  value?: T | null;
  onChange?: (value: T | null) => void;
  placeholder?: ReactNode;
  required?: boolean;
  caret?: boolean;
  searchOnFocus?: boolean;
  noResultsLabel?: string;
};

export function Select<T extends string | number = string | number>({
  options: optionsProvider,
  value,
  onChange,
  placeholder,
  required,
  caret = true,
  searchOnFocus = true,
  noResultsLabel = 'No options',
  className,
}: PropsWithClassName<SelectProps<T>>) {
  const [selectedOption, setSelectedOption] = useState<Option<T> | null>(() => {
    if (!isFunction(optionsProvider)) {
      return optionsProvider.find((option) => option.value == value) ?? null;
    }

    return null;
  });

  useEffect(() => {
    if (value === selectedOption?.value) {
      return;
    }

    if (!value) {
      setSelectedOption(null);

      return;
    }

    if (!isFunction(optionsProvider)) {
      setSelectedOption(optionsProvider.find((option) => option.value == value) ?? null);

      return;
    }

    optionsProvider({ value }).then((options) => {
      if (options[0]) {
        setSelectedOption(options[0]);
      }
    });
  }, [value]);

  function handleChange(option: Option<T> | null) {
    setSelectedOption(option);
    onChange?.(option?.value ?? null);
  }

  const selectProps: StateManagerProps<Option<T>, false> = {
    value: selectedOption,
    placeholder: placeholder ?? '',
    required: required,
    onChange: handleChange,
    openMenuOnFocus: true,
    menuPlacement: 'auto',
    classNames: {
      container: () => cn('font-normal rounded border bg-white outline-none focus:border-indigo-500', className),
      control: () =>
        'border-none rounded-none bg-transparent shadow-none min-h-0 focus-within:border-indigo-400 w-full h-full',
      valueContainer: () => 'px-3 py-2',
      input: () => 'm-0 p-0',
      menu: () => 'my-1',
      menuList: () => 'p-0 rounded-none',
      singleValue: () => 'm-0',
      option: (state) =>
        cn('first:rounded-t last:rounded-b px-3 py-2', {
          'bg-indigo-100': state.isFocused,
          'bg-indigo-500': state.isSelected,
        }),
    },
    components: {
      DropdownIndicator: () =>
        caret ? (
          <button type="button" className="mr-2 text-gray-400">
            <Icon icon="angle-down" />
          </button>
        ) : null,
      IndicatorSeparator: null,
      NoOptionsMessage: () => <div className="px-3 py-2 rounded text-gray-400">{noResultsLabel ?? 'No options'}</div>,
      LoadingMessage: () => <div className="px-3 py-2 rounded text-gray-400">Loading…</div>,
    },
  };

  function loadOptions(search: string, callback: (options: Options<T>) => void): void {
    if (isFunction(optionsProvider)) {
      optionsProvider({ search }).then(callback);
    }
  }

  const [defaultOptions, setDefaultOptions] = useState<Options<T>>([]);

  function loadDefaultOptions() {
    if (!searchOnFocus) {
      return;
    }

    if (isFunction(optionsProvider)) {
      optionsProvider({ search: '' }).then(setDefaultOptions);
    }
  }

  if (isFunction(optionsProvider)) {
    return (
      <AsyncReactSelect
        cacheOptions
        loadOptions={loadOptions}
        onMenuOpen={loadDefaultOptions}
        defaultOptions={defaultOptions}
        {...selectProps}
      />
    );
  }

  return <ReactSelect options={optionsProvider} {...selectProps} />;
}
